<template>
    <PageHeader :title="$t('product_selection.title', { type: componentTypeLabel.toUpperCase() })" @back="goBack" />

    <div class="flex-1 space-y-4 overflow-y-auto p-4">
        <div>
            <input
                v-model="searchQuery"
                type="text"
                data-testid="product-search-input"
                :placeholder="$t('product_selection.search_placeholder', { type: componentTypeLabel })"
                class="border-stroke-1 bg-card w-full border p-3 text-white placeholder-gray-400 outline-none focus:border-red-500 focus:ring-2 focus:ring-red-500"
                @input="handleSearch"
            />
        </div>

        <div v-if="isLoading" class="py-8 text-center text-gray-400">{{ $t('product_selection.loading') }}</div>
        <div v-else-if="products.length === 0" class="py-8 text-center text-gray-400">
            {{ searchQuery ? $t('product_selection.no_results') : $t('product_selection.start_typing') }}
        </div>
        <div v-else class="space-y-3">
            <button
                v-for="product in products"
                :key="product.id"
                @click="selectProduct(product)"
                data-testid="product-result"
                class="border-stroke-1 bg-bg hover:bg-card flex w-full items-center gap-4 border p-4 text-left"
            >
                <div v-if="product.image_url" class="h-16 w-16 shrink-0 overflow-hidden rounded">
                    <img
                        :src="getImageUrl(product.image_url)"
                        :alt="product.name"
                        class="h-full w-full bg-white object-contain p-1"
                    />
                </div>
                <div
                    v-else
                    class="flex h-16 w-16 shrink-0 items-center justify-center rounded bg-zinc-700"
                >
                    <svg
                        class="h-8 w-8 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                        />
                    </svg>
                </div>
                <div class="min-w-0 flex-1">
                    <div class="font-medium text-white">{{ product.name }}</div>
                    <div class="text-xs text-gray-400 capitalize">{{ productTypeLabel(product.type) }}</div>
                </div>
                <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    class="text-gray-400"
                >
                    <path d="M9 18l6-6-6-6" />
                </svg>
            </button>
        </div>
    </div>
</template>

<script>
import { setupService } from '@/services/setup.js'
import PageHeader from '@/components/PageHeader.vue'

export default {
    name: 'ProductSelectionView',
    components: {
        PageHeader,
    },
    computed: {
        component() {
            return this.$route.query.component
        },
        componentType() {
            const types = {
                tip: 'point',
                barrel: 'barrel',
                shaft: 'shaft',
                flight: 'flight',
                dart: 'dart',
            }
            return types[this.component] || this.component
        },
        componentTypeLabel() {
            const keys = {
                point: 'product_selection.type_point',
                barrel: 'product_selection.type_barrel',
                shaft: 'product_selection.type_shaft',
                flight: 'product_selection.type_flight',
                dart: 'product_selection.type_dart',
            }
            return keys[this.componentType] ? this.$t(keys[this.componentType]) : this.componentType
        },
    },
    data() {
        return {
            searchQuery: '',
            products: [],
            isLoading: false,
            searchTimeout: null,
        }
    },
    mounted() {
        this.handleSearch()
    },
    beforeUnmount() {
        if (this.searchTimeout) {
            clearTimeout(this.searchTimeout)
        }
    },
    methods: {
        productTypeLabel(type) {
            const keys = {
                tip: 'setup_edit.point_label',
                point: 'setup_edit.point_label',
                barrel: 'setup_edit.barrel_label',
                shaft: 'setup_edit.shaft_label',
                flight: 'setup_edit.flight_label',
                dart: 'setup_edit.dart_label',
            }
            return keys[type] ? this.$t(keys[type]) : type
        },
        async handleSearch() {
            if (this.searchTimeout) {
                clearTimeout(this.searchTimeout)
            }
            this.searchTimeout = setTimeout(async () => {
                this.isLoading = true
                try {
                    const response = await setupService.searchProducts(
                        this.searchQuery,
                        this.componentType,
                    )
                    this.products = response.data || []
                } catch (error) {
                    console.error('Failed to search products:', error)
                    this.products = []
                } finally {
                    this.isLoading = false
                }
            }, 300)
        },
        async selectProduct(product) {
            let productId = product.id

            if (product.source === 'graphql') {
                try {
                    this.isLoading = true
                    const productWithType = {
                        ...product,
                        type: this.componentType,
                    }
                    const response = await setupService.saveExternalProduct(productWithType)
                    if (response.success && response.data) {
                        productId = response.data.id
                    } else {
                        this.$toast(this.$t('product_selection.error_save'), 3000)
                        return
                    }
                } catch (error) {
                    console.error('Error saving external product:', error)
                    this.$toast(this.$t('product_selection.error_save_title'), 3000)
                    return
                } finally {
                    this.isLoading = false
                }
            }

            const returnRoute = this.$route.query.return || 'setup-create'
            const query = {
                component: this.component,
                productId: productId,
            }

            this.$router.replace({
                name: returnRoute,
                params: returnRoute === 'setup-edit' ? { id: this.$route.query.setupId } : {},
                query,
            })
        },
        getImageUrl(imageUrl) {
            if (!imageUrl) return ''
            if (imageUrl.startsWith('http')) return imageUrl
            const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || ''
            const baseUrl = apiBaseUrl.replace(/\/api\/?$/, '')
            return `${baseUrl}/${imageUrl}`
        },
        goBack() {
            const returnRoute = this.$route.query.return || 'setup-create'

            if (returnRoute === 'setup-edit' && this.$route.query.setupId) {
                this.$router.replace({
                    name: 'setup-edit',
                    params: { id: this.$route.query.setupId },
                })
            } else {
                this.$router.replace({ name: 'setup-create' })
            }
        },
    },
}
</script>
